import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { HazardsSelectorComponent } from 'src/app/hazards/hazards-selector/hazards-selector.component';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { HazardsEditComponent } from 'src/app/hazards/hazards-edit/hazards-edit.component';
import { HazardModel } from 'src/app/models/hazard.model';
import { SitesHazardsViewComponent } from '../sites-hazards-view/sites-hazards-view.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import {SitesHazardsFilterComponent} from "../sites-hazards-filter/sites-hazards-filter.component";
import { HazardEditPermissions } from '../../../interfaces/hazard-edit-permissions.interface';

@Component({
    selector: 'app-sites-hazards',
    templateUrl: './sites-hazards.component.html',
    styleUrls: ['./sites-hazards.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SitesHazardsComponent implements OnInit, AfterViewInit {

  parent_id: number;
  child_id: number;
  site_id: number;

  // columns to show and the data source
  displayedColumns: string[] = [];
  dataSource: SiteHazardsDataSource = new SiteHazardsDataSource(this.app, this.api, true);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canView: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canViewHazardTemplates: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    public route: ActivatedRoute,
    private api: ApiRequestService,
    private router: Router,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    // Resolve the RBAC permission flags before anything else so they are always set.
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true);
    this.canViewHazardTemplates = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true);

    // Check sites:view parent permission
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES, RBACService.RBAC_A.A_VIEW)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access this site.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // Check if user has ANY sites-hazards-risks permission (exact=false)
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], false)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access site hazards/risks.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // Build displayedColumns dynamically
    this.displayedColumns = [
      'id',
      'site',
      'name',
      'status',
      'ira_score',
      'ira',
      'rra_score',
      'rra',
      'controls',
      'date_created',
      'reviewer_date',
      'actions'
    ];

    // Add select column if user has view permission (needed for export selection and bulk delete)
    if (this.canView) {
      this.displayedColumns.unshift('select');
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if ( this.child_id || (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' && this.parent_id) ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;
      // init the data source
      this.dataSource.site_id = this.site_id;
      this.dataSource.site_ids.push(this.site_id);

      // Only load data if user has view permission
      if (this.canView) {
        this.dataSource.getData(true);
      }
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active == "date_created_UTC") {
        this.dataSource.sort_by = "date_created";
      } else {
        this.dataSource.sort_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onView(hazard: HazardModel) {
    // Layer 2 guard: the row button is inside the view-gated table, but the method can also be
    // called programmatically, so it enforces the permission itself.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view hazards/risks.');
      return;
    }

    this.utils.showComponentDialog(
      SitesHazardsViewComponent,
      { id: hazard.id },
      { width: '900px' },
      (success) => {
        if (!success) { return; }
        this.dataSource.getData(true);
      }
    );
  }

  onAdd() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create site hazards/risks.');
      return;
    }
    const hazard = new HazardModel();
    hazard.site_id = this.site_id;
    const permissions: HazardEditPermissions = {
      canSave: true,
      canDelete: this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
      ], true),
      canCopySites: false,
      canSelectReviewer: true
    };
    // Open the edit dialog directly: bypasses onEdit()'s view+edit permission check
    this.utils.showComponentDialog(
      HazardsEditComponent,
      { ...hazard, _permissions: permissions },
      {},
      (success) => {
        if (this.rbac.userCanAccess([
          RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
        ], true)) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onEdit(hazard: HazardModel) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_EDIT)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit site hazards/risks.');
      return;
    }
    const permissions: HazardEditPermissions = {
      canSave: true,
      canDelete: this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
      ], true),
      canCopySites: false,
      canSelectReviewer: true
    };
    // open the hazard edit dialog for editing
    this.utils.showComponentDialog(
      HazardsEditComponent,
      { ...hazard, _permissions: permissions },
      {},
      (success) => {
        // if (!success) { return; } // Removed to update list on close
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * @param id the id of the hazard.
   * Remove a single hazard.
   */
  onRemove(id: number) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete site hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Archive Hazard/Risk',
      'Are you sure you want to archive this Hazard/Risk?',
      () => {
        this.onRemoveHazards([id], (response) => {
          this.dataSource.selection.deselect(id);
        });
      }
    );
  }

  onRestore(id: number) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore site hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Restore Hazard/Risk',
      'Are you sure you want to restore this Hazard/Risk?',
      () => {
        this.onRestoreHazards([id], (response) => {
          this.dataSource.selection.deselect(id);
        });
      }
    );
  }

  /**
   * Remove selected hazards from the specified site
   */
  onDeleteSelected() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete site hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Archive Selected Hazards & Risks',
      'Are you sure you want to archive the selected Hazards & Risks?',
      () => {
        this.onRemoveHazards(this.dataSource.selection.selected, (response) => {
          this.dataSource.selection.clear();
        });
      }
    );
  }

  onRestoreSelected() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore site hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Hazards & Risks',
      'Are you sure you want to restore the selected Hazards & Risks?',
      () => {
        this.onRestoreHazards(this.dataSource.selection.selected, (response) => {
          this.dataSource.selection.clear();
        });
      }
    );
  }

  /**
   * Open a dialog to select hazards.
   */
  onSelectHazards() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to copy hazard/risk templates.');
      return;
    }
    // show the hazard selector
    this.utils.showComponentDialog(
      HazardsSelectorComponent,
      {
        multiple: true
      }, {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.copyHazardsToSite(results);
        }
      }
    );
  }

  copyHazardsToSite(selected_hazard_ids) {
    this.api.makeRequest(
      'put',
      'v1/sites/' + this.site_id + '/hazards',
      {
        selected_hazard_ids: selected_hazard_ids
      }
    ).then((response: any) => {
      this.dataSource.getData(true);
      this.utils.showToast('Hazards/Risks were copied to the ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site') + '.');
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
    });
  }

  onRemoveHazards(hazard_ids: number[], callback?: any) {
    if ( this.dataSource.site_ids.length ) {
      // Remove hazards
      this.api.makeRequest(
        'delete',
        'v1/hazards/' + hazard_ids.join(',') + '?site_id=' + this.dataSource.site_ids.join(',')
      ).then((response: any) => {
        // reload the data and reset the offset
        this.dataSource.getData(true);

        this.utils.showToast('The selected Hazards/Risks were archived.');

        if (typeof callback === 'function') {
          callback(response);
        }
      }).catch((err: any) => {
        this.utils.showModal('Error', err.error.message);
      });
    }
  }

  onRestoreHazards(hazard_ids: number[], callback?: any) {
    if ( this.dataSource.site_ids.length > 0 ) {
      // Restore hazards.
      this.api.makeRequest(
        'put',
        'v1/hazards/' + hazard_ids.join(',') + '/restore?site_id=' + this.dataSource.site_ids.join(',')
      ).then((response: any) => {
        // reload the data and reset the offset
        this.dataSource.getData(true);
        this.utils.showToast('The selected Hazards/Risks were restored.');

        if (typeof callback === 'function') {
          callback(response);
        }
      }).catch((err: any) => {
        this.utils.showModal('Error', err.error.message);
      });
    }
  }

  /**
   * Exports the list of hazards into the specified format and sends a download request to the browser.
   * @param type csv, xls, xlsx, pdf.
   * Last updated by Kobus Beets on 13/1/2021.
   */
  onExportSelected(type: string = 'xlsx') {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export site hazards/risks.');
      return;
    }
    this.api.makeDownloadRequest(`v1/hazards/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {}, {
      site_ids: this.site_id,
      order_by: this.dataSource.sort_by,
      order: this.dataSource.sort_order
    })
      .then((response) => {
        // handle internet explorer - No longer supported
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(response);
        //   return;
        // }

        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download = `hazards-risks-${date.getFullYear()}${(date.getMonth() + 1)}${date.getDate()}.${type}`;
        // Initiate the download
        downloadLink.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function () {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 miliseconds
      });
  }

  onCreateFrom(hazard: HazardModel) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create site hazards/risks.');
      return;
    }
    const newHazard: HazardModel = new HazardModel();
    newHazard.createFrom(hazard);
    newHazard.site_id = this.site_id;
    const permissions: HazardEditPermissions = {
      canSave: true,
      canDelete: this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
      ], true),
      canCopySites: false,
      canSelectReviewer: true
    };
    // Open the edit dialog directly: bypasses onEdit()'s view+edit permission check
    this.utils.showComponentDialog(
      HazardsEditComponent,
      { ...newHazard, _permissions: permissions },
      {},
      (success) => {
        if (this.rbac.userCanAccess([
          RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
        ], true)) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onFilter() {
    this.utils.showComponentDialog(
      SitesHazardsFilterComponent,
      {
        selected_site_ids: this.dataSource.site_ids,
        archived: this.dataSource.archived,
        parent_site_id: this.parent_id
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids;
          this.dataSource.archived = results.archived || this.dataSource.archived;

          if ( this.dataSource.site_ids.length === 0 ) {
            this.dataSource.site_ids.push(this.site_id);
          }

          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Calculates and returns the count of hazard controls.
   *
   * @param {any} controls - The array or object containing hazard controls.
   * @return {number} The number of hazard controls. Returns 0 if the input does not contain any controls.
   */
  getHazardControlsCount(controls: any): number {
    if ( controls.length ) {
      return controls.length;
    }
    return 0;
  }

  /**
   * Retrieves the site information from the provided hazard object.
   *
   * @param {any} hazard - The hazard object containing site information.
   * @return {any|null} The site information from the hazard object, or null if not found.
   */
  getHazardSite(hazard: any): any | null {
    return hazard.site || hazard.asset?.site || null;
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}

export class SiteHazardsDataSource extends CustomDataSource {

  sort_by = 'name';
  sort_order = 'asc';

  site_id: number = 0;

  site_ids = [];

  archived = 'false';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'sites/' + this.site_id + '/hazards',
      resetOffset,
      () => { },
      {
        eager_load: true,
        site_ids: this.site_ids.join(','),
        archived: this.archived
      }
    );
  }
}
